import { resolveIcon } from "./iconRegistry";

/**
 * Drop-in replacement for the old `<i className="ri-xxx-line ...">` icon-font
 * pattern. Renders bundled Remix Icon SVGs (from @remixicon/react) instead of
 * relying on an externally-loaded icon font stylesheet, so icons keep working
 * even when the CDN stylesheet can't be reached (restrictive networks, ad
 * blockers, sandboxed preview environments, ...).
 *
 * Usage is unchanged from the old `<i>` markup — just swap the tag name:
 *   <Icon className="ri-exchange-line text-2xl text-white" />
 *
 * Tailwind text-size classes (text-xl, text-2xl, ...) still control the
 * visual size because the icon is rendered at size="1em", and text-color
 * classes still control the color because Remix icons fill with
 * `currentColor` by default.
 *
 * The set of available icons lives in ./iconRegistry.ts.
 */
interface IconProps {
  className?: string;
  [key: string]: unknown;
}

export default function Icon({ className = "", ...rest }: IconProps) {
  const tokens = className.split(/\s+/).filter(Boolean);
  const iconToken = tokens.find((t) => t.startsWith("ri-"));
  const passthroughClasses = tokens.filter((t) => t !== iconToken).join(" ");
  const IconComponent = resolveIcon(iconToken);

  if (!IconComponent) return null;

  return <IconComponent size="1em" className={passthroughClasses} {...rest} />;
}
